import { useQuery } from '@apollo/client';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import type { CuiOptionType } from '@crehana/ui';
import { notEmpty } from '@crehana/utils';

import useAdminDashboard from '@/views/AdminDashboard/hooks/useAdminDashboard';

import useDebounce from '../useDebounce';
import GET_B2B_CUSTOM_FIELDS_USER_TAGS from './graphql/GetB2bCustomFieldsUserTags.v4.b2b.graphql';
import GET_B2B_CUSTOM_FIELDS_USER_TAGS_ALL from './graphql/GetB2bCustomFieldsUserTagsAll.v4.b2b.graphql';
import type {
  GetB2bCustomFieldsUserTags,
  GetB2bCustomFieldsUserTagsVariables,
} from './graphql/types/GetB2bCustomFieldsUserTags';
import type {
  GetB2bCustomFieldsUserTagsAll,
  GetB2bCustomFieldsUserTagsAllVariables,
} from './graphql/types/GetB2bCustomFieldsUserTagsAll';

const ITEMS_PER_FETCH = 8;

interface IUseOrgCustomFieldsUserTagsProps {
  customFieldId?: string;
  isFilter?: boolean;
}

const useOrgCustomFieldsUserTags = ({
  customFieldId,
  isFilter,
}: IUseOrgCustomFieldsUserTagsProps) => {
  const { slug } = useAdminDashboard();
  const { debounceValue: searchKey, handleChange } = useDebounce();

  const nonNullCustomFieldId = customFieldId || '';

  const { data, refetch, loading, error } = useQuery<
    GetB2bCustomFieldsUserTags,
    GetB2bCustomFieldsUserTagsVariables
  >(GET_B2B_CUSTOM_FIELDS_USER_TAGS, {
    variables: {
      slug,
      searchKey,
      customFieldId: nonNullCustomFieldId,
      isFilter,
      first: ITEMS_PER_FETCH,
      after: '',
    },
    skip: !customFieldId,
    context: { clientName: 'v4.b2b' },
    fetchPolicy: 'no-cache',
  });

  const { data: allData } = useQuery<
    GetB2bCustomFieldsUserTagsAll,
    GetB2bCustomFieldsUserTagsAllVariables
  >(GET_B2B_CUSTOM_FIELDS_USER_TAGS_ALL, {
    variables: {
      slug,
      searchKey,
      customFieldId: nonNullCustomFieldId,
      isFilter,
    },
    skip: !customFieldId,
    context: { clientName: 'v4.b2b' },
    fetchPolicy: 'no-cache',
  });

  const totalCount = data?.b2bCustomFieldsUserTags?.totalCount || 0;

  const items = data?.b2bCustomFieldsUserTags?.edges?.filter(notEmpty) || [];

  const itemsAsOptions = items.map(
    el =>
      ({
        label: el.node.answer,
        value: el.node.answer,
      }) as CuiOptionType<string>,
  );

  const allIds =
    allData?.b2bCustomFieldsUserTags?.edges
      ?.map(el => el.node.organizationUserId)
      .filter(notEmpty) || [];

  const pageIds =
    items?.map(el => el?.node?.organizationUserId).filter(notEmpty) || [];

  return {
    data,
    error,
    pageIds,
    allIds,
    refetch,
    loading,
    searchKey,
    handleChange,
    items,
    itemsAsOptions,
    totalCount,
    batchSize: ITEMS_PER_FETCH,
  };
};

export default useOrgCustomFieldsUserTags;
